import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Truck, CheckCircle, WarningCircle, Spinner, CurrencyEur, MapPin, CalendarBlank } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceRequestPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [used, setUsed] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    billing_name: '',
    billing_email: '',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    country: 'France',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/api/invoice-request/${token}`);
        if (response.data.used) {
          setUsed(true);
        } else {
          setData(response.data);
          setForm(prev => ({
            ...prev,
            billing_name: response.data.client_name || '',
            billing_email: response.data.client_email || '',
          }));
        }
      } catch (err) {
        setError('Lien invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.billing_name || !form.billing_email || !form.address_line1 || !form.postal_code || !form.city) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/invoice-request/${token}`, form);
      setSubmitted(response.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setUsed(true);
      } else {
        setError(err.response?.data?.detail || 'Erreur lors de la soumission');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-green-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <WarningCircle size={48} className="mx-auto text-red-400 mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (used) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <CheckCircle size={48} weight="fill" className="mx-auto text-green-500 mb-4" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Demande déjà enregistrée</h2>
          <p className="text-gray-500">Ce lien a déjà été utilisé. Votre demande de facture a bien été enregistrée.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <CheckCircle size={48} weight="fill" className="mx-auto text-green-500 mb-4" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Demande enregistrée</h2>
          <p className="text-gray-500">
            Votre facture vous sera envoyée par email à <strong>{submitted.email}</strong> dans quelques instants.
          </p>
          {submitted.invoice_number && (
            <p className="text-xs text-gray-400 mt-3">Facture n° {submitted.invoice_number}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-lg mx-auto px-4 flex items-center gap-3">
          <Truck size={24} weight="fill" className="text-green-600" />
          <h1 className="text-lg font-bold text-gray-900">
            <span className="text-green-600">reco</span>pneus
          </h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Demande de facture</h2>
          <p className="text-sm text-gray-500 mt-1">Remplissez vos informations de facturation</p>
        </div>

        {/* Intervention summary (read-only) */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Récapitulatif</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <CalendarBlank size={16} className="text-gray-400" />
              <span>{data.intervention_date}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin size={16} className="text-gray-400" />
              <span>{data.address}</span>
            </div>
            <div className="flex items-center gap-2 text-green-700 font-semibold text-lg">
              <CurrencyEur size={18} />
              <span>{data.price?.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Billing form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Vos informations</h3>
            <div className="space-y-2">
              <Label>Nom / Prénom *</Label>
              <Input
                value={form.billing_name}
                onChange={(e) => setForm(p => ({ ...p, billing_name: e.target.value }))}
                required
                className="bg-white border-gray-300"
                data-testid="billing-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse email *</Label>
              <Input
                type="email"
                value={form.billing_email}
                onChange={(e) => setForm(p => ({ ...p, billing_email: e.target.value }))}
                required
                className="bg-white border-gray-300"
                data-testid="billing-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse *</Label>
              <Input
                value={form.address_line1}
                onChange={(e) => setForm(p => ({ ...p, address_line1: e.target.value }))}
                required
                placeholder="100 Avenue Franklin Roosevelt"
                className="bg-white border-gray-300"
                data-testid="billing-address-line1"
              />
            </div>
            <div className="space-y-2">
              <Label>Informations complémentaires <span className="text-gray-400 font-normal">(optionnel)</span></Label>
              <Input
                value={form.address_line2}
                onChange={(e) => setForm(p => ({ ...p, address_line2: e.target.value }))}
                placeholder="Appartement, étage, bâtiment..."
                className="bg-white border-gray-300"
                data-testid="billing-address-line2"
              />
            </div>
            <div className="grid grid-cols-10 gap-3">
              <div className="col-span-3 space-y-2">
                <Label>Code postal *</Label>
                <Input
                  value={form.postal_code}
                  onChange={(e) => setForm(p => ({ ...p, postal_code: e.target.value.replace(/[^0-9]/g, '').slice(0, 5) }))}
                  required
                  inputMode="numeric"
                  placeholder="06110"
                  className="bg-white border-gray-300"
                  data-testid="billing-postal-code"
                />
              </div>
              <div className="col-span-7 space-y-2">
                <Label>Ville *</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
                  required
                  placeholder="Le Cannet"
                  className="bg-white border-gray-300"
                  data-testid="billing-city"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pays *</Label>
              <Input
                value={form.country}
                onChange={(e) => setForm(p => ({ ...p, country: e.target.value }))}
                required
                placeholder="France"
                className="bg-white border-gray-300"
                data-testid="billing-country"
              />
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <WarningCircle size={20} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Attention : une fois ces informations validées, aucune modification ne sera possible. Vérifiez attentivement vos informations avant de confirmer.
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting || !form.billing_name || !form.billing_email || !form.address_line1 || !form.postal_code || !form.city}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-3 text-base font-semibold"
            data-testid="submit-invoice-request"
          >
            {submitting ? 'Envoi en cours...' : 'Confirmer ma demande de facture'}
          </Button>
        </form>
      </main>
    </div>
  );
}
